import { humanizeBn } from 'tools/math';
import { IDexPair } from 'types/pairs';

import { convertTokenDEX } from './convertToken';

type Params = {
  pair: IDexPair;
  totalAmount: string;
  totalCurrency: string;
  minAmount: string;
  maxAmount: string;
  tradesCurrency: string;
  nativeQuote?: { native_amount_in: string; base_amount_out: string };
};

// basePerNative returns base tokens per one native token from the on-chain native route quote.
const basePerNative = (pair: IDexPair, quote?: Params['nativeQuote']) => {
  if (!quote) return 0;
  const nativeIn = Number(quote.native_amount_in) / 10 ** pair.token_fee.decimals;
  const baseOut = Number(quote.base_amount_out) / 10 ** pair.token_base.decimals;
  return nativeIn > 0 && baseOut > 0 ? baseOut / nativeIn : 0;
};

export const normalizeBuySellEstimateAmounts = ({
  pair,
  totalAmount,
  totalCurrency,
  minAmount,
  maxAmount,
  tradesCurrency,
  nativeQuote,
}: Params) => {
  const rate = basePerNative(pair, nativeQuote);
  // A base token without USD price converts to 0. The native route quote then gives the rate.
  const toBaseByQuote = (amount: string, from: string) => {
    const value = Number(amount);
    if (!(value > 0) || !(rate > 0)) return undefined;
    if (from === pair.token_fee.symbol) return String(value * rate);
    const nativeUsd = Number(pair.token_fee.price_usd) / 1e6;
    if (from === 'USD' && nativeUsd > 0) return String((value / nativeUsd) * rate);
    return undefined;
  };

  const toBase = (amount: string, from: string) => {
    const converted = humanizeBn(
      convertTokenDEX({
        from,
        to: pair.token_base.symbol,
        pair,
        amount,
        decimals: pair.token_base.decimals,
      }),
      pair.token_base.decimals,
    );
    if (Number(converted) > 0) return converted;
    return toBaseByQuote(amount, from) ?? converted;
  };

  // Finish time and fee costs use only the ratio of the total to the trade amount.
  // In one currency the ratio needs no conversion, so a base token without USD price still gives an estimate.
  if (totalCurrency === tradesCurrency) {
    return { totalAmount, minAmount, maxAmount };
  }

  return {
    totalAmount: toBase(totalAmount, totalCurrency),
    minAmount: toBase(minAmount, tradesCurrency),
    maxAmount: toBase(maxAmount, tradesCurrency),
  };
};
